import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { apiFetch } from "@/lib/api";

// Add or edit a manual time entry. `entry` present = edit mode (preserves audit
// history + shows an "edit note" server-side).
export function ManualTimeDialog({ open, onOpenChange, tasks = [], entry = null, defaultTaskId = "", onSaved }) {
  const [acts, setActs] = useState([]);
  const [form, setForm] = useState(blank(defaultTaskId));
  const [editNote, setEditNote] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    apiFetch("/time/activity-types").then(setActs).catch(() => {});
    if (entry) {
      setForm({ task_id: entry.task_id, entry_date: entry.entry_date, subtask_id: entry.subtask_id || "",
        duration_minutes: String(entry.duration_minutes || ""), activity_type: entry.activity_type || "", note: entry.note || "" });
    } else {
      setForm(blank(defaultTaskId));
    }
    setEditNote("");
  }, [open, entry, defaultTaskId]);

  const save = async () => {
    if (!form.task_id) return toast.error("Please choose a task.");
    const mins = Number(form.duration_minutes);
    if (!mins || mins <= 0) return toast.error("Please enter a duration greater than zero.");
    setSaving(true);
    try {
      if (entry) {
        await apiFetch(`/time/entries/${entry.id}`, { method: "PUT", body: JSON.stringify({
          duration_minutes: mins, activity_type: form.activity_type, note: form.note,
          entry_date: form.entry_date, edit_note: editNote }) });
        toast.success("Time entry updated");
      } else {
        await apiFetch("/time/entries", { method: "POST", body: JSON.stringify({
          task_id: form.task_id, subtask_id: form.subtask_id, entry_date: form.entry_date,
          duration_minutes: mins, activity_type: form.activity_type, note: form.note }) });
        toast.success("Time added");
      }
      onOpenChange(false); onSaved?.();
    } catch (e) { toast.error(e.message); } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="manual-time-dialog">
        <DialogHeader><DialogTitle className="font-heading text-2xl">{entry ? "Edit time entry" : "Add time manually"}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Task</Label>
            <Select value={form.task_id} onValueChange={(v) => setForm({ ...form, task_id: v })} disabled={!!entry}>
              <SelectTrigger data-testid="manual-task"><SelectValue placeholder="Choose a task" /></SelectTrigger>
              <SelectContent>{tasks.map((t) => <SelectItem key={t.id} value={t.id}>{t.title}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div><Label className="text-xs">Date</Label><Input type="date" value={form.entry_date} onChange={(e) => setForm({ ...form, entry_date: e.target.value })} data-testid="manual-date" /></div>
            <div><Label className="text-xs">Duration (minutes)</Label><Input type="number" min="1" value={form.duration_minutes} onChange={(e) => setForm({ ...form, duration_minutes: e.target.value })} data-testid="manual-minutes" placeholder="e.g. 45" /></div>
          </div>
          <div>
            <Label className="text-xs">Activity type</Label>
            <Select value={form.activity_type || "none"} onValueChange={(v) => setForm({ ...form, activity_type: v === "none" ? "" : v })}>
              <SelectTrigger data-testid="manual-activity"><SelectValue placeholder="Optional" /></SelectTrigger>
              <SelectContent><SelectItem value="none">Unspecified</SelectItem>{acts.map((a) => <SelectItem key={a.id} value={a.name}>{a.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label className="text-xs">Note (optional)</Label><Textarea rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} data-testid="manual-note" /></div>
          {entry && <div><Label className="text-xs">Reason for edit (optional)</Label><Input value={editNote} onChange={(e) => setEditNote(e.target.value)} placeholder="Kept for the audit trail" data-testid="manual-edit-note" /></div>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving} data-testid="manual-save">{saving && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}Save</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function blank(taskId) {
  return { task_id: taskId || "", subtask_id: "", entry_date: new Date().toISOString().slice(0, 10),
    duration_minutes: "", activity_type: "", note: "" };
}
